import { useEffect, useState } from 'react';
import { differenceInYears, differenceInMonths, isValid, parse } from 'date-fns';
import { cn } from '../../lib/utils';
import { Input } from '../ui/input';

/**
 * Date-of-Birth picker.
 *
 * Was: a shadcn Calendar popover on desktop / native <input type="date"> on mobile.
 * Now: a single text input that accepts MM/DD/YYYY. The popover calendar was
 * intermittently failing on Desktop Safari (the date-fns dropdown caption +
 * Radix Popover combination produced a stale render of the month grid); typing
 * is also faster than scrolling through months for a known birthdate.
 *
 * Wire format is unchanged: the component always emits ISO "YYYY-MM-DD" via
 * onChange so every consumer (LearnersPage, GuestCheckoutForm, Admin pages,
 * RegistrationPage) keeps working without a contract change.
 *
 * Inline behaviour:
 *   - Auto-inserts "/" after MM and DD as the user types.
 *   - Validates after blur (or once 10 chars are typed) — shows a red error
 *     row when the date is malformed, in the future, or out of range.
 *   - Renders an "Age: X years, Y months" hint when the parse succeeds, so
 *     year-typos (2008 vs 2018) are caught instantly.
 */
export default function DobPicker({
  value,            // ISO "YYYY-MM-DD" string ('' / undefined when empty)
  onChange,         // (iso: string) => void
  placeholder = 'MM/DD/YYYY',
  testId = 'dob-picker',
  minYear,
  maxYear,
  showAge = true,
}) {
  const today = new Date();
  const defaultMax = maxYear ?? today.getFullYear();
  const defaultMin = minYear ?? defaultMax - 100;

  // Local display string in MM/DD/YYYY shape. Initialised from the ISO prop
  // so existing learner records display correctly when the form re-opens.
  const [display, setDisplay] = useState(() => isoToMmddyyyy(value));
  const [error, setError] = useState('');

  // Keep the display string in sync if the parent resets `value`.
  useEffect(() => {
    setDisplay(isoToMmddyyyy(value));
  }, [value]);

  const validate = (mmddyyyy) => {
    if (!mmddyyyy) return { ok: false, err: '' }; // empty = no error (other validators handle 'required')
    if (mmddyyyy.length !== 10) {
      return { ok: false, err: 'Use MM/DD/YYYY format.' };
    }
    const parsed = parse(mmddyyyy, 'MM/dd/yyyy', new Date());
    if (!isValid(parsed)) return { ok: false, err: 'Enter a valid date.' };
    if (parsed > today) return { ok: false, err: "Date can't be in the future." };
    const yr = parsed.getFullYear();
    if (yr < defaultMin || yr > defaultMax) {
      return { ok: false, err: `Year must be ${defaultMin}–${defaultMax}.` };
    }
    return { ok: true, parsed };
  };

  const handleInput = (e) => {
    const masked = applyMask(e.target.value);
    setDisplay(masked);

    // Live emit only when we have a fully-typed, parseable date.
    if (masked.length === 10) {
      const { ok, parsed } = validate(masked);
      if (ok) {
        onChange(toIso(parsed));
        setError('');
      } else if (!error) {
        // Wait for blur to surface errors, but clear an existing error
        // if the user is editing toward a valid date.
        // (no-op)
      }
    } else if (value) {
      // User started editing — clear the stored ISO until they finish.
      onChange('');
    }
  };

  const handleBlur = () => {
    if (!display) { setError(''); return; }
    const result = validate(display);
    setError(result.ok ? '' : result.err);
    if (result.ok) onChange(toIso(result.parsed));
  };

  // Live age hint — only when we have a stored ISO value.
  const ageLabel = (() => {
    if (!showAge || !value) return null;
    const d = parse(value, 'yyyy-MM-dd', new Date());
    if (!isValid(d) || d > today) return null;
    const years = differenceInYears(today, d);
    const months = differenceInMonths(today, d) - years * 12;
    const parts = [];
    if (years > 0) parts.push(`${years} year${years === 1 ? '' : 's'}`);
    parts.push(`${months} month${months === 1 ? '' : 's'}`);
    return parts.join(', ');
  })();

  return (
    <div>
      {/* Eyebrow note — explicit format requirement above the field. */}
      <p
        className="text-[11px] text-muted-foreground mb-1 font-medium tracking-wide uppercase"
        data-testid={`${testId}-format-hint`}
      >
        MM/DD/YYYY
      </p>
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="bday"
        maxLength={10}
        value={display}
        placeholder={placeholder}
        onChange={handleInput}
        onBlur={handleBlur}
        className={cn(error && 'border-red-500 focus-visible:ring-red-500')}
        data-testid={testId}
        aria-describedby={`${testId}-format-hint`}
        aria-invalid={!!error || undefined}
      />
      {error && (
        <p className="text-xs text-red-600 mt-1.5" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
      {ageLabel && !error && (
        <p
          className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1"
          data-testid={`${testId}-age-label`}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Age: <strong className="text-foreground">{ageLabel}</strong>
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Convert ISO "YYYY-MM-DD" → display "MM/DD/YYYY". Empty input returns ''. */
function isoToMmddyyyy(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  return `${m[2]}/${m[3]}/${m[1]}`;
}

/** Convert a Date → ISO "YYYY-MM-DD" using local date parts (no UTC drift). */
function toIso(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Mask raw typed input into MM/DD/YYYY shape.
 *   - Strips non-digits, then re-inserts slashes after positions 2 and 4.
 *   - Caps at 8 digits (MMDDYYYY) so the result never exceeds 10 chars.
 *   - Accepts pasted strings with existing slashes or hyphens by stripping them first.
 */
function applyMask(raw) {
  const digits = (raw || '').replace(/\D+/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}
